import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import queryString from 'query-string';

import CustomBreadcrumb from 'components/base/CustomBreadcrumb';
import {
  getCampaignCreateSuccessStepBar,
  getCampaignStepBarNameWithType,
} from 'components/base/prompt/NextStepConfig';
import BaseForm from 'components/base/v2/BaseForm';
import {
  URLFilterKeys,
  URLFragments,
} from 'components/campaign/CampaignUrlConfig';
import { createCampaignBreads } from 'config/CustomBreadConfig';
import { APIStatus, CampaignType } from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import {
  removeDataFromSessionStorage,
  saveDataToSessionStorage,
  sessionDataKey,
} from 'models/CreateCampaignModel';

import CreateCampaignTypePage from 'components/campaignStreamline/CreateCampaignTypePage';
import CreateCampaignContentPage from 'components/campaignStreamline/CreateCampaignContentPage';
import CreateCampaignPreviewPage from 'components/campaignStreamline/CreateCampaignPreviewPage';
import CreateCampaignCouponPage from 'components/campaignStreamline/CreateCampaignCouponPage';
import CreateCampaignRulePage from 'components/campaignStreamline/CreateCampaignRulePage';

const CreateCampaign = ({
  stepWithCoupon,
  stepConfig,
  currentStep,
  campaign = {},
  linkedEarningRules,
  linkedCoupon,
  campaignPublish,
  formHasSubmitted,
  hasUpdatedDefaultValues,
}) => {
  const params = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const search = queryString.parse(history.location.search);

  const campaignType = campaign.campaignType;
  const campaignId = campaign.pk;
  const campaignGeneralName = campaign.translations?.en?.generalName;

  useEffect(() => {
    dispatch({ type: 'createCampaign/clearData' });
    dispatch({ type: 'campaignStreamline/clearData' })
    if (!history.location.search) {
      const campaignId = params.id;
      if (campaignId) {
        dispatch({ type: 'createCampaign/getOneCampaign', payload: { id: campaignId } });
      } else {
        dispatch({
          type: 'createCampaign/loadCampaignFromCookie',
          payload: {
            resume: location.state?.resume,
          }
        });
      }
      dispatch({ type: 'brand/getCurrentPageBrands', payload: {} });
    }
    dispatch({ type: 'language/getList', payload: { isSelectorLoad: true } });
    dispatch({ type: 'settings/getAdminTipConfig' });
    return () => {
      dispatch({ type: 'createCampaign/clearData' });
      dispatch({ type: 'campaignStreamline/clearData' })
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stepSet = stepWithCoupon
    ? [
      <CreateCampaignTypePage />,
      <CreateCampaignContentPage />,
      <CreateCampaignRulePage />,
      <CreateCampaignCouponPage />,
      <CreateCampaignPreviewPage />,
    ]
    : [
      <CreateCampaignTypePage />,
      <CreateCampaignContentPage />,
      <CreateCampaignRulePage />,
      <CreateCampaignPreviewPage />,
    ];

  const toCampaignList = () => {
    const search = queryString.stringify({
      [URLFilterKeys.type]: CampaignType.allTypes,
      [URLFilterKeys.page]: 1,
      [URLFilterKeys.searchKey]: '',
      [URLFilterKeys.rank]: true,
    });
    history.push({
      pathname: '/campaigns',
      hash: URLFragments.list,
      search: search,
    });
  };

  const getCompleteSteps = () => { // twitch TODO
    const urlCampaignType = search.type;
    const urlId = search.id;
    const linkedId =
      CampaignType.couponsCampaign === campaignType
        ? linkedCoupon?.pk
        : linkedEarningRules?.pk;
    let showMoreSteps = location.search;
    if (urlCampaignType !== campaignType) {
      showMoreSteps = false;
    }
    if (urlId + '' !== linkedId + '') {
      showMoreSteps = false;
    }
    return getCampaignCreateSuccessStepBar(campaignType, showMoreSteps);
  };

  return (
    <BaseForm
      useStepsV2
      stepOnClick={(step) => {
        dispatch({ type: 'campaignStreamline/stepChange', payload: { step: step } })
      }}
      defaultValues={campaign}
      formHasSubmitted={formHasSubmitted}
      hasUpdatedDefaultValues={hasUpdatedDefaultValues}
      tempSave={(save, getValues) => {
        if (save) {
          saveDataToSessionStorage(getValues());
        } else {
          removeDataFromSessionStorage();
        }
      }}
      showFinishPop={campaignPublish === APIStatus.success}
      nextStepConfig={{
        title: 'Successfully Published!',
        description:
          'The campaign is successfully published.\n Let your customers know about the campaign.',
        steps: {
          stepNames: getCompleteSteps(),
          current: getCampaignStepBarNameWithType(campaignType),
        },
        onHide: () => { },
        buttons: [
          {
            text: 'Create message',
            action: () => {
              history.push({
                pathname: '/messages/create',
                search: `${location.search}&campaign=${campaignId}${location.search ? '' : '&type=' + campaignType
                  }`,
              });
            },
            requires: PermissionCodes.addMessage,
          },
          {
            text: 'Maybe Later',
            action: toCampaignList,
          },
        ],
      }}
      continueCreateConfig={{
        onConfirm: () => {
          history.push(history.location.state?.from);
        },
        onCancel: toCampaignList,
        onClose: toCampaignList,
      }}
      sessionDataKey={sessionDataKey}
      content={stepSet}
      currentStep={currentStep}
      breadcrumb={
        <CustomBreadcrumb
          breadcrumbs={createCampaignBreads(
            campaignId ? campaignGeneralName : 'Create Campaign',
          )}
        />
      }
      stepsConfig={stepConfig}
      caution={{
        detail: '',
        title: campaignId ? campaignGeneralName : 'Create Campaign',
      }}
      hasleavePagePrompt={params.id}
    />
  );
}

const mapStateToProps = (state) => ({
  stepWithCoupon: state.campaignStreamline.stepWithCoupon,
  stepConfig: state.campaignStreamline.stepConfig,
  currentStep: state.campaignStreamline.currentStep,
  campaign: state.createCampaign.campaign,
  linkedEarningRules: state.createCampaign.campaign?.linkedEarningRules,
  linkedCoupon: state.createCampaign.campaign?.linkedCoupon,
  campaignPublish: state.createCampaign.campaignPublish,
  formHasSubmitted: state.createCampaign.formHasSubmitted,
  hasUpdatedDefaultValues: state.createCampaign.hasUpdatedDefaultValues,
});

export default connect(mapStateToProps)(CreateCampaign);
